import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  DjiDroneRef,
  DjiFlightDetail,
  DjiFlightFilters,
  DjiFlightPage,
  DjiFlightRow,
  DjiLinkStatus,
  DjiRelinkResult,
  DjiSetDroneRequest,
  DjiStatusCounts,
  DjiUnlinkTarget,
  OrganizationOption,
} from '../models/dji-flight.model';
import { hubErrorMessage, toListQuery } from '../utils/dji-flight.utils';

/** The Hub's shared answer: `{ status, data, errors[] }`, with `paging` and `statusCounts` on the list. */
interface ResponseModel<T> {
  status: number;
  data: T;
  errors?: string[] | string;
}

interface ListResponseModel extends ResponseModel<DjiFlightRow[]> {
  paging: { page: number; pageSize: number; totalItems: number; totalPages: number };
  statusCounts: DjiStatusCounts;
}

/**
 * DjiFlightAdminService (ADN-2451)
 *
 * Wraps the Hub's `/admin/dji` endpoints (ADN-2450): DJI flights across every
 * organization, and the actions that resolve them. Every failure reaches the
 * caller as an `Error` carrying the Hub's own message, so the screens can show
 * it as is.
 */
@Injectable({ providedIn: 'root' })
export class DjiFlightAdminService {
  private readonly base = `${environment.apiUrl}/admin/dji`;

  constructor(private http: HttpClient) {}

  listFlights(filters: DjiFlightFilters = {}): Observable<DjiFlightPage> {
    const params = new HttpParams({ fromObject: toListQuery(filters) });
    return this.http.get<ListResponseModel>(`${this.base}/flights`, { params }).pipe(
      map((r) => ({
        flights: r.data ?? [],
        total: r.paging?.totalItems ?? 0,
        page: r.paging?.page ?? 1,
        pageSize: r.paging?.pageSize ?? 25,
        totalPages: r.paging?.totalPages ?? 0,
        statusCounts: r.statusCounts,
      })),
      this.failWith('Failed to load DJI flights.'),
    );
  }

  getFlight(intakeId: string): Observable<DjiFlightDetail> {
    return this.http.get<ResponseModel<DjiFlightDetail>>(`${this.base}/flights/${intakeId}`).pipe(
      map((r) => r.data),
      this.failWith('Failed to load this flight.'),
    );
  }

  /** Any drone by serial number or name (two characters or more). */
  searchDrones(search: string): Observable<DjiDroneRef[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<ResponseModel<DjiDroneRef[]>>(`${this.base}/drones`, { params }).pipe(
      map((r) => r.data ?? []),
      this.failWith('Failed to search drones.'),
    );
  }

  setDroneAndOrganization(intakeId: string, body: DjiSetDroneRequest): Observable<DjiLinkStatus> {
    return this.action<{ linkStatus: DjiLinkStatus }>(intakeId, 'set-drone', body, 'Failed to set the drone.').pipe(map((d) => d.linkStatus));
  }

  unlink(intakeId: string, target: DjiUnlinkTarget): Observable<DjiLinkStatus> {
    return this.action<{ linkStatus: DjiLinkStatus }>(intakeId, 'unlink', { target }, 'Failed to unlink.').pipe(map((d) => d.linkStatus));
  }

  dismiss(intakeId: string, note?: string): Observable<DjiLinkStatus> {
    return this.action<{ linkStatus: DjiLinkStatus }>(intakeId, 'dismiss', { note }, 'Failed to dismiss the flight.').pipe(map((d) => d.linkStatus));
  }

  markInvalid(intakeId: string, note?: string): Observable<DjiLinkStatus> {
    return this.action<{ linkStatus: DjiLinkStatus }>(intakeId, 'mark-invalid', { note }, 'Failed to mark the flight invalid.').pipe(
      map((d) => d.linkStatus),
    );
  }

  restore(intakeId: string): Observable<DjiLinkStatus> {
    return this.action<{ linkStatus: DjiLinkStatus }>(intakeId, 'restore', {}, 'Failed to restore the flight.').pipe(map((d) => d.linkStatus));
  }

  relink(intakeId: string): Observable<DjiRelinkResult> {
    return this.action<DjiRelinkResult>(intakeId, 'relink', {}, 'Failed to re-run linkage.');
  }

  /** The organizations for the filter and the drone picker. */
  listOrganizations(): Observable<OrganizationOption[]> {
    return this.http
      .get<{ success: boolean; data?: { organizations: OrganizationOption[] } }>(`${environment.apiUrl}/admin/organizations?pageSize=500`)
      .pipe(
        map((r) => (r.data?.organizations ?? []).map((o) => ({ id: o.id, name: o.name }))),
        this.failWith('Failed to load organizations.'),
      );
  }

  private action<T>(intakeId: string, name: string, body: object, fallback: string): Observable<T> {
    return this.http.post<ResponseModel<T>>(`${this.base}/flights/${intakeId}/${name}`, body).pipe(
      map((r) => r.data),
      this.failWith(fallback),
    );
  }

  /** Turns an HTTP failure into an `Error` whose message is the Hub's. */
  private failWith<T>(fallback: string) {
    return catchError<T, Observable<never>>((error) => throwError(() => new Error(hubErrorMessage(error, fallback))));
  }
}
